import React from "react";
import {Button} from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {Separator} from "@/components/ui/separator";
import {getCriteriaValue, keyValueMapping} from "@/utils/criteria-helper.tsx";
import {getTimeAgoString} from "@/utils/time-helper.ts";
import {Preference} from "@/types/Preference.ts";
import {AllCriteria, Criteria} from "@/types/Criteria.ts";
import {useTranslation} from "react-i18next";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";

interface PreferenceDetailsProps {
    criteria: AllCriteria | undefined;
    deadline?: Date;
    preference: Preference;
    onEdit?: () => void;
    onDelete?: (preference: Preference) => void;
    showActions?: boolean;
}

export const PreferenceDetails: React.FC<PreferenceDetailsProps> = ({
                                                                        criteria,
                                                                        deadline,
                                                                        preference,
                                                                        onEdit,
                                                                        onDelete,
                                                                        showActions = true
                                                                    }) => {
    const {t} = useTranslation();

    const getLocalizedLabel = (key: string) => t(`criteria.${keyValueMapping[key].label}`);

    return (
        <Card className="overflow-hidden border border-border bg-bg-card shadow-sm">
            <CardHeader className="border-b border-border">
                <div className="flex items-center justify-between gap-4">
                    <CardTitle className="text-lg font-semibold text-text-1">
                        {t("preference.savedPreference")}
                    </CardTitle>
                    {showActions && (
                        <div className="flex items-center gap-2">
                            <Button 
                                size="sm" 
                                variant="default" 
                                className="h-9 bg-text-1 text-bg-card hover:opacity-95 rounded-md px-3" 
                                onClick={onEdit}
                            >
                                {t("common.edit")}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                className="h-9 bg-status-danger text-white hover:opacity-95 rounded-md px-3 font-medium"
                                onClick={() => onDelete?.(preference)}
                            >
                                {t("common.delete")}
                            </Button>
                        </div>
                    )}
                </div>
                {showActions && (
                    <CardDescription className="text-text-2 mt-1">
                        {t('preference.updatePreferenceDeadline', {deadline: deadline?.toDateString()})}
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent className="pt-6 text-sm">
                <div className="grid gap-3">
                    <div className="font-semibold text-text-1">{t("criteria.language")}</div>
                    <div className="text-text-2">
                        {preference.language
                            ? preference.language[getLocalizedFieldName('name') as keyof Criteria]
                            : t("preference.noPreference")}
                    </div>
                </div>
                <div className="grid gap-3 mt-4">
                    <div className="font-semibold text-text-1">{t("affinity.group")}</div>
                    <div className="text-text-2">
                        {(preference.affinityGroups ?? []).length > 0
                            ? preference.affinityGroups?.map((item) =>
                                item.affinityGroup[getLocalizedFieldName("name") as keyof typeof item.affinityGroup]
                            ).join(", ")
                            : t("common.none")}
                    </div>
                </div>
                <Separator className="my-6 bg-border"/>
                <div className="grid sm:grid-cols-2 sm:gap-16">
                    <div className="grid gap-4">
                        <div className="font-semibold text-text-1">{t("criteria.broadCriteria")}</div>
                        <ul className="grid gap-3 text-text-2">
                            <li className="flex items-center justify-between">
                                <span>
                                    {getLocalizedLabel(preference.b1Key)}
                                </span>
                                <span className="text-right font-medium text-text-1">
                                    {getCriteriaValue(criteria, preference.b1Key, preference.b1Value)}
                                </span>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>
                                    {getLocalizedLabel(preference.b2Key)}
                                </span>
                                <span className="text-right font-medium text-text-1">
                                    {getCriteriaValue(criteria, preference.b2Key, preference.b2Value)}
                                </span>
                            </li>
                            <li className="flex items-center justify-between">
                                <span>
                                    {getLocalizedLabel(preference.b3Key)}
                                </span>
                                <span className="text-right font-medium text-text-1">
                                    {getCriteriaValue(criteria, preference.b3Key, preference.b3Value)}
                                </span>
                            </li>
                        </ul>
                    </div>
                    <div className="mt-4 sm:mt-0 grid auto-rows-max gap-4">
                        <div className="font-semibold text-text-1">{t("criteria.specificCriteria")}</div>
                        {!preference.s1Key ? (
                            <div className="text-text-2">{t("common.none")}</div>
                        ) : (
                            <ul className="grid gap-3 text-text-2">
                                <li className="flex items-center justify-between">
                                    <span>
                                        {getLocalizedLabel(preference.s1Key)}
                                    </span>
                                    <span className="text-right font-medium text-text-1">
                                        {getCriteriaValue(criteria, preference.s1Key, preference.s1Value)}
                                    </span>
                                </li>
                                {preference.s2Key && (
                                    <li className="flex items-center justify-between">
                                        <span>
                                            {getLocalizedLabel(preference.s2Key)}
                                        </span>
                                        <span className="text-right font-medium text-text-1">
                                            {getCriteriaValue(criteria, preference.s2Key, preference.s2Value)}
                                        </span>
                                    </li>
                                )}
                                {preference.s3Key && (
                                    <li className="flex items-center justify-between">
                                        <span>
                                            {getLocalizedLabel(preference.s3Key)}
                                        </span>
                                        <span className="text-right font-medium text-text-1">
                                            {getCriteriaValue(criteria, preference.s3Key, preference.s3Value)}
                                        </span>
                                    </li>
                                )}
                                {preference.s4Key && (
                                    <li className="flex items-center justify-between">
                                        <span>
                                            {getLocalizedLabel(preference.s4Key)}
                                        </span>
                                        <span className="text-right font-medium text-text-1">
                                            {getCriteriaValue(criteria, preference.s4Key, preference.s4Value)}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </CardContent>
            <CardFooter className="flex flex-row items-center border-t border-border bg-bg-elevated/60 px-6 py-3">
                <div className="text-xs text-text-3">
                    {t("preference.lastUpdate", {timeAgo: getTimeAgoString(preference.updatedAt, t)})}
                </div>
            </CardFooter>
        </Card>
    )
}
